import { AccountLevelEnum, AccountStatusEnum, ButtonTypeEnum, SnackBarVariants, UserRole } from '@app/app.enum';
import store from '@app/app.store';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import Icon from '@app/components/shared/icon.component';
import PasswordTextFieldComponent from '@app/components/shared/password-text-field.component';
import * as constants from '@app/constants';
import * as models from '@app/models';
import { IMenuInjectedProps } from '@app/models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { AdminUserService } from '@app/services/admin-user.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { UserService } from '@app/services/user.service';
import {
	Badge,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemSecondaryAction,
	Menu,
	MenuItem,
	TextField,
	Typography,
} from '@material-ui/core';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import * as React from 'react';
import { FormattedDate, injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import * as Yup from 'yup';

interface IUpdateEmailFields {
	email: string;
}
interface IGenerateTempPasswordField {
	password: string;
}

const accountService: AccountService = Injector.get(AccountService);
const intlService: IntlService = Injector.get(IntlService);
const userService: UserService = Injector.get(UserService);
const sharedService: SharedService = Injector.get(SharedService);
const adminUserService: AdminUserService = Injector.get(AdminUserService);

const UpdateEmailSchema = Yup.object().shape<IUpdateEmailFields>({
	email: Yup.string()
		.trim()
		.email(intlService.GetFormattedMessage('app.signup.signup-form.email-invalid-validation'))
		.required(intlService.GetFormattedMessage('app.signup.signup-form.email-required-validation')),
});

const generateTempPassSchema = Yup.object().shape<IGenerateTempPasswordField>({
	password: Yup.string().required(intlService.GetFormattedMessage('app.login.signin-password-required-validation')),
});

const addUserSchema = Yup.object().shape<models.IAddUser>({
	firstName: Yup.string().required(
		intlService.GetFormattedMessage('app.profile.user-info-form.first-name-required-validation')
	),
	lastName: Yup.string().required(
		intlService.GetFormattedMessage('app.profile.user-info-form.last-name-required-validation')
	),
	email: Yup.string()
		.email(intlService.GetFormattedMessage('app.login.signin-email-invalid-validation'))
		.required(intlService.GetFormattedMessage('app.login.signin-email-required-validation')),
	role: Yup.mixed<UserRole>().required(
		intlService.GetFormattedMessage('app.profile.user-info-form.close-account-validation')
	),
});

class AdminAccountUserListComponent extends React.Component<
	models.IAdminAccountUserListProps,
	models.IAdminAccountUserListState
> {
	public formRef = React.createRef<Formik<FormikValues>>();
	public isPartner: boolean;
	private generateTempPasswordField: IGenerateTempPasswordField = {
		password: '',
	};
	private initialAddUserFormValue = models.initAddUser();

	constructor(props: models.IAdminAccountUserListProps) {
		super(props);
		this.state = models.initialAdminAccountUserListState();
		this.isPartner = store.getState().authentication.user.account.level === AccountLevelEnum.Partner;
	}

	public render() {
		const usersListView = this.generateUsersListView(this.props.users);
		return (
			<React.Fragment>
				{/* DialogBox for invite of users */}
				<CustomDialogBox
					open={this.state.openInviteDialog}
					dialogTitle={intlService.GetFormattedMessage('app.user.resend-invite')}
					dialogDescription={intlService.GetFormattedMessage('app.user.resend-invite-dialog-description')}
					closeCustomDialog={async (dialogString?: string, isCancel?: boolean) => {
						if (!isCancel) {
							await userService.resendUserInvitation(this.state.inviteId);
							this.props.openSnackBar(intlService.GetFormattedMessage('app.snackbar.user-invitation-sent-successful'));
						}
						this.setState({
							inviteId: '',
							openInviteDialog: false,
						});
					}}
					dialogYesButton={intlService.GetFormattedMessage('app.usps-tier-config-default-yes-label')}
					dialogNoButton={intlService.GetFormattedMessage('app.usps-tier-config-default-no-label')}
				/>

				{/*  Users list */}
				<Grid container={true} spacing={1} alignItems='center'>
					{!this.isPartner && this.renderAddUserButtonWithDialog()}
					<Grid item={true} md={6} lg={8}>
						<TextField
							fullWidth={true}
							variant='outlined'
							placeholder={intlService.GetFormattedMessage('admin-account-user-list.search-users-placeholder')}
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Icon name='magnifying-glass' />
									</InputAdornment>
								),
							}}
							value={this.state.userSearchString}
							onChange={(e) => {
								this.setState({
									userSearchString: e.target.value,
								});
							}}
							size='small'
						/>
					</Grid>
					<Grid container={true}>
						<Grid item={true} xs={12}>
							{this.getUsersList(usersListView)}
						</Grid>
					</Grid>
				</Grid>
				{this.renderDialogBoxes()}
			</React.Fragment>
		);
	}

	private renderDialogBoxes(): React.ReactNode {
		return (
			<React.Fragment>
				{this.renderUpdateEmailDialog()}
				{this.renderDeleteCarrierDialog()}
				{this.renderPasswordResetDialog()}
				{this.renderAdminPasswordVerificationDialog()}
				{this.renderTemporaryPasswordDialog()}
			</React.Fragment>
		);
	}

	private renderAdminPasswordVerificationDialog() {
		return (
			<CustomDialogBox
				open={!!this.state.isAdminPasswordVerificationDialogOpen}
				dialogTitle={intlService.GetFormattedMessage('admin-account-user-list.verify-password-dialog-title')}
				dialogDescription={null}
				closeCustomDialog={() => {
					this.setState({
						isAdminPasswordVerificationDialogOpen: false,
					});
				}}
				fullWidth={true}
				submitButton={{
					isLoading: this.state.isTempPasswordGeneratedLoading,
					isSubmit: true,
					onSubmit: () => this.formRef.current.submitForm(),
					isDisabled: this.state.isTempPasswordGeneratedLoading,
				}}
				dialogYesButton={intlService.GetFormattedMessage('app.common.dialog-confirm-button-text')}
			>
				<Typography variant='body1'>
					{intlService.GetFormattedMessage('admin-account-user-list.generate-temp-password-dialog-description')}
				</Typography>
				<Formik
					initialValues={this.generateTempPasswordField}
					ref={this.formRef}
					onSubmit={async () => await this.generateTemparoryPassword()}
					validationSchema={generateTempPassSchema}
					render={(props: FormikProps<FormikValues>) => this.renderTemporaryPasswordForm(props)}
				/>
			</CustomDialogBox>
		);
	}

	private renderPasswordResetDialog() {
		return (
			<CustomDialogBox
				open={!!this.state.isOpenResetDialog}
				dialogTitle={intlService.GetFormattedMessage('app.reset.password.reset-password-form.reset-password-label')}
				dialogDescription={this.props.intl.formatHTMLMessage(
					{
						id: 'admin-account-user-list.reset-password-dialog-description',
					},
					{
						0: this.state.email,
					}
				)}
				closeCustomDialog={(dialogPannel: string, isCancel: boolean) => {
					if (!isCancel) {
						this.onResetPassword(this.state.email);
					}
					this.setState({
						isOpenResetDialog: false,
					});
				}}
				fullWidth={true}
				dialogYesButton={intlService.GetFormattedMessage('app.login.forgot.password.send-button')}
			/>
		);
	}

	private renderDeleteCarrierDialog() {
		return (
			<CustomDialogBox
				open={!!this.state.isOpenDeleteDialog}
				dialogTitle={intlService.GetFormattedMessage('app.user.delete-dialog-title')}
				dialogDescription={intlService.GetFormattedMessage('app.user.delete-dialog-description')}
				closeCustomDialog={(dialogPannel: string, isCancel: boolean) => {
					if (!isCancel) {
						this.deleteUser(this.state.userId);
					}
					this.setState({
						isOpenDeleteDialog: false,
					});
				}}
			/>
		);
	}

	private renderUpdateEmailDialog() {
		return (
			<CustomDialogBox
				open={!!this.state.isOpenEditDialog}
				dialogTitle={intlService.GetFormattedMessage('app.user.update-user-label')}
				dialogDescription={intlService.GetFormattedMessage('admin-account-user-list.update-email-dialog-description')}
				closeCustomDialog={(dialogPannel: string, isCancel: boolean) => {
					this.setState({
						isOpenEditDialog: false,
					});
				}}
				submitButton={{
					isSubmit: true,
					isLoading: this.state.editUserLoading,
					onSubmit: () => this.formRef.current.submitForm(),
					isDisabled: this.state.editUserLoading,
				}}
				dialogYesButton={intlService.GetFormattedMessage('app.user.save-button')}
				fullWidth={true}
			>
				<Formik
					initialValues={{ email: this.state.email }}
					ref={this.formRef}
					onSubmit={() => this.UpdateUserEmailByAdmin(this.state.userId, this.state.email)}
					validationSchema={UpdateEmailSchema}
					render={(props: FormikProps<FormikValues>) => this.renderUpdateUserEmailForm(props)}
				/>
			</CustomDialogBox>
		);
	}

	private renderUpdateUserEmailForm(props: FormikProps<FormikValues>): React.ReactNode {
		return (
			<Form noValidate={true}>
				<Field
					label={intlService.GetFormattedMessage('app.signup.email-label')}
					name='email'
					margin='normal'
					variant='outlined'
					component={TextField}
					type='text'
					required={true}
					fullWidth={true}
					value={props.values.email}
					onChange={(e: any) => {
						this.setState({
							email: e.target.value,
						});
						props.handleChange('email')(e);
					}}
					error={!!((props.values.email.length || props.submitCount) && !!props.errors.email)}
					helperText={props.values.email.length || props.submitCount ? props.errors.email : null}
				/>
			</Form>
		);
	}

	private renderTemporaryPasswordForm(props: FormikProps<FormikValues>): React.ReactNode {
		return (
			<Form noValidate={true}>
				<Field
					name='password'
					label={this.props.intl.formatMessage({
						id: 'app.login.password-label',
					})}
					margin='normal'
					variant='outlined'
					component={PasswordTextFieldComponent}
					value={props.values.password}
					type='password'
					required={true}
					fullWidth={true}
					onChange={(e: any) => {
						this.setState({
							password: e.target.value,
						});
						props.handleChange('password')(e);
					}}
					error={!!((props.values.password.length || props.submitCount) && !!props.errors.password)}
					helperText={props.values.password.length || props.submitCount ? props.errors.password : null}
				/>
			</Form>
		);
	}

	private async generateTemparoryPassword() {
		try {
			const request: models.IAdminPasswordVerificationRequest = {
				id: this.state.userId,
				passwordAdmin: this.state.password,
			};
			this.setState({
				isTempPasswordGeneratedLoading: true,
			});
			// verify admin password before generating temp password
			const res = await adminUserService.verifyAndGenerateTempPassword(request);

			this.setState({
				isTempPasswordGeneratedLoading: false,
				isAdminPasswordVerificationDialogOpen: false,
				isGeneratedPasswordDialogOpen: true,
				passwordAdmin: res.passwordAdmin,
				userName: res.userName,
				passwordAdminExpiresOn: res.passwordAdminExpiresOn,
			});
			this.openSnackbar(
				this.props.intl.formatMessage({
					id: 'admin-account-user-list.admin-temp-password-generated-successfully',
				}),
				SnackBarVariants.Success
			);
		} catch (error) {
			this.setState({
				isTempPasswordGeneratedLoading: false,
			});
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private generateUsersListView(users: models.IAdminAccountUserTreeTableData[]): React.ReactNode {
		return users?.length > 0 ? (
			<List>
				{users
					.filter(
						(user) =>
							!this.state.userSearchString ||
							user.name.toLowerCase().includes(this.state.userSearchString.toLowerCase()) ||
							user.email.toLowerCase().includes(this.state.userSearchString.toLowerCase()) ||
							user.userName.toLowerCase().includes(this.state.userSearchString.toLowerCase())
					)
					.map((user) => this.generateUserListView(user))}
			</List>
		) : (
			<Typography align='center'>{intlService.GetFormattedMessage('app.shipment.no-data-available')}</Typography>
		);
	}

	private generateUserListView = (user: models.IAdminAccountUserTreeTableData): React.ReactNode => (
		<React.Fragment key={user.id}>
			<ListItem button={true} selected={this.state.collapse[user.id]} dense={true}>
				<Grid container={true}>
					<Grid item={true} xs={11} onClick={() => this.getUserDetails(user.id)} className='pt-1 pb-1'>
						{this.renderUserListRow(user)}
					</Grid>
					<Grid item={true} xs={1}>
						{!this.isPartner && (
							<ListItemSecondaryAction>
								<PopupState variant='popper'>
									{(popupState: IMenuInjectedProps) => (
										<React.Fragment>
											<IconButton
												disabled={this.props.accountStatus === AccountStatusEnum.Closed}
												{...bindTrigger(popupState)}
											>
												<Icon name='ellipsis-vertical' className='icon-dimension-square' />
											</IconButton>
											{this.renderUserListViewMenuItems(user, popupState)}
										</React.Fragment>
									)}
								</PopupState>
							</ListItemSecondaryAction>
						)}
					</Grid>
				</Grid>
			</ListItem>
			<Collapse
				in={this.state.collapse[user.id]}
				timeout='auto'
				unmountOnExit={true}
				classes={{ wrapper: 'pt-1 pb-1' }}
			>
				{this.renderUserDetails(user)}
			</Collapse>
			<Divider variant='fullWidth' />
		</React.Fragment>
	);

	private renderUserListRow(user: models.IAdminAccountUserTreeTableData): React.ReactNode {
		return (
			<Grid container={true} direction='row'>
				<Grid item={true} xs={4}>
					{this.renderUserRoleBadge(user)}
				</Grid>
				<Grid item={true} xs={4}>
					{user.userName || '-'}
				</Grid>
				<Grid item={true} xs={4}>
					{user.email}
				</Grid>
			</Grid>
		);
	}

	private renderUserRoleBadge(user: models.IAdminAccountUserTreeTableData): React.ReactNode {
		switch (user.userRole) {
			case UserRole.AccountAdmin:
				return (
					<Badge badgeContent={user.userRole} className='pr-5'>
						{user.name}
					</Badge>
				);
			case UserRole.Admin:
				return (
					<Badge badgeContent='You' className='pr-2'>
						{user.name}
					</Badge>
				);
			case UserRole.ReadOnlyAdmin:
				return (
					<Badge badgeContent='you' className='pr-5'>
						{user.name}
					</Badge>
				);
			case UserRole.User:
				return user.name;
		}
	}

	private renderUserListViewMenuItems(
		user: models.IAdminAccountUserTreeTableData,
		popupState: models.IMenuInjectedProps
	): React.ReactNode {
		return (
			<Menu {...bindMenu(popupState)}>
				<MenuItem
					onClick={() => {
						this.setState({
							isOpenEditDialog: true,
							email: user.email,
							userId: user.id,
						});
						popupState.close();
					}}
				>
					{intlService.GetFormattedMessage('app.user.update-label')}
				</MenuItem>
				{user.userRole === UserRole.User && (
					<MenuItem
						onClick={() => {
							this.setState({
								isOpenDeleteDialog: true,
								userId: user.id,
							});
							popupState.close();
						}}
					>
						{intlService.GetFormattedMessage('app.user.delete')}
					</MenuItem>
				)}
				<MenuItem
					onClick={() => {
						this.setState({
							isOpenResetDialog: true,
							email: user.email,
						});
						popupState.close();
					}}
				>
					{intlService.GetFormattedMessage('admin-account-user-list.reset-password-button-label')}
				</MenuItem>
				{user.userRole === UserRole.User && !user.isActivated && (
					<MenuItem
						onClick={() => {
							this.setState({
								openInviteDialog: true,
								inviteId: user.id,
							});
							popupState.close();
						}}
					>
						{intlService.GetFormattedMessage('app.user.resend-invite')}
					</MenuItem>
				)}
				<MenuItem
					onClick={() => {
						this.setState({
							isAdminPasswordVerificationDialogOpen: true,
							userId: user.id,
							email: user.email,
						});
						popupState.close();
					}}
				>
					{intlService.GetFormattedMessage('admin-account-user-list.generate-temp-password-button-label')}
				</MenuItem>
			</Menu>
		);
	}

	private getUsersList = (users: React.ReactNode): React.ReactNode => {
		return (
			<React.Fragment>
				<ListItem>
					<Grid container={true}>
						<Grid item={true} xl={11} lg={11} md={11} sm={11} xs={11}>
							<Grid container={true} direction='row'>
								<Grid item={true} xs={4}>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'usps-tier-config.rate-set-name-label',
										})}`}
									</strong>
								</Grid>
								<Grid item={true} xs={4}>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'app.profile.user-info-form.username-label',
										})}`}
									</strong>
								</Grid>
								<Grid item={true} xs={4}>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'app.profile.user-info-form.email-label',
										})}`}
									</strong>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</ListItem>
				{this.state.dataLoading ? (
					<div className='spinner-for-panels'>
						<CircularProgress size={32} />
					</div>
				) : (
					users
				)}
			</React.Fragment>
		);
	};

	private renderTemporaryPasswordDialog = () => {
		return (
			<React.Fragment>
				<Dialog
					open={!!this.state.isGeneratedPasswordDialogOpen}
					onClose={() =>
						this.setState({
							isGeneratedPasswordDialogOpen: false,
						})
					}
				>
					<DialogTitle>
						{intlService.GetFormattedMessage('admin-account-user-list.temp-password-dialog-title')}
					</DialogTitle>
					<DialogContent>
						<Grid container={true}>
							<Grid item={true} xs={12}>
								<Grid container={true} spacing={2}>
									<Grid item={true} xs={4}>
										<Typography color='primary' variant='body2' component={'span'}>
											<strong>
												{`${this.props.intl.formatMessage({
													id: 'app.profile.user-info-form.email-label',
												})}`}
											</strong>
										</Typography>
									</Grid>
									<Grid item={true} xs={8}>
										<Typography variant='body2' component={'span'}>
											{this.state.email}
										</Typography>
									</Grid>
									<Grid item={true} xs={4}>
										<Typography color='primary' variant='body2' component={'span'}>
											<strong>
												{`${this.props.intl.formatMessage({
													id: 'app.profile.user-info-form.username-label',
												})}`}
											</strong>
										</Typography>
									</Grid>
									<Grid item={true} xs={8}>
										<Typography variant='body2' component={'span'}>
											{this.state.userName}
										</Typography>
									</Grid>
									<Grid item={true} xs={12}>
										<TextField
											label={
												<strong>
													{`${this.props.intl.formatMessage({
														id: 'admin-account-user-list.temp-password-label',
													})}`}
												</strong>
											}
											variant='outlined'
											size='small'
											type='text'
											fullWidth={true}
											value={this.state.passwordAdmin}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														<IconButton onClick={() => this.copyToClipboard(this.state.passwordAdmin)}>
															<Icon name='file-copy' />
														</IconButton>
													</InputAdornment>
												),
												readOnly: true,
											}}
											helperText={
												<>
													{this.props.intl.formatHTMLMessage({
														id: 'admin-account-user-list.temp-password-helper-text',
													})}
													<FormattedDate
														{...constants.dateTimeFormatOptions}
														value={sharedService.getNewDateIfNotExist(this.state.passwordAdminExpiresOn)}
													/>
												</>
											}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								this.setState({
									isGeneratedPasswordDialogOpen: false,
								});
							}}
							color='secondary'
						>
							{intlService.GetFormattedMessage('app.common.dialog-okay-button-text')}
						</Button>
					</DialogActions>
				</Dialog>
			</React.Fragment>
		);
	};

	private renderAddUserButtonWithDialog(): React.ReactNode {
		return (
			<React.Fragment>
				{this.addUserFormDialog()}
				<Grid item={true} md={6} lg={4}>
					<Button
						startIcon={<Icon name='plus' />}
						fullWidth={true}
						disabled={this.props.accountStatus === AccountStatusEnum.Closed}
						variant='text'
						onClick={() => {
							this.setState({
								isOpenAddUserDialog: true,
							});
						}}
					>
						<strong>{intlService.GetFormattedMessage('app.user.add-user-label')}</strong>
					</Button>
				</Grid>
			</React.Fragment>
		);
	}

	private renderUserDetails = (user: models.IAccountUserResponseVM) => {
		return (
			<>
				<Grid container={true}>
					<Grid item={true} xs={12}>
						<Grid container={true} spacing={2}>
							<Grid item={true} xs={2}>
								<Typography variant='body2' className='userDetails-padding-left'>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'usps-tier-config.rate-set-name-label',
										})}`}
									</strong>
								</Typography>
							</Grid>
							<Grid item={true} xs={4}>
								<Typography variant='body2'>{user.name}</Typography>
							</Grid>
							<Grid item={true} xs={2}>
								<Typography variant='body2' className='userDetails-padding-left'>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'app.profile.user-info-form.username-label',
										})}`}
									</strong>
								</Typography>
							</Grid>
							<Grid item={true} xs={4}>
								<Typography variant='body2'>{user.userName}</Typography>
							</Grid>
							<Grid item={true} xs={2}>
								<Typography variant='body2' className='userDetails-padding-left'>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'app.profile.user-info-form.email-label',
										})}`}
									</strong>
								</Typography>
							</Grid>
							<Grid item={true} xs={4}>
								<Typography variant='body2'>{user.email}</Typography>
							</Grid>
							<Grid item={true} xs={2}>
								<Typography variant='body2' className='userDetails-padding-left'>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'app.user.role-label',
										})}`}
									</strong>
								</Typography>
							</Grid>
							<Grid item={true} xs={4}>
								<Typography variant='body2'>{user.userRole}</Typography>
							</Grid>
							<Grid item={true} xs={2}>
								<Typography variant='body2' className='userDetails-padding-left'>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'app.customer-last-login-date',
										})}`}
									</strong>
								</Typography>
							</Grid>
							<Grid item={true} xs={4} sm={4} md={4}>
								<Typography variant='body2'>
									{
										<FormattedDate
											{...constants.dateFormatOptions}
											value={sharedService.getNewDateIfNotExist(user.lastLoginDate)}
										/>
									}
								</Typography>
							</Grid>
							<Grid item={true} xs={2}>
								<Typography variant='body2' className='userDetails-padding-left'>
									<strong>
										{`${this.props.intl.formatMessage({
											id: 'app.customer-joining-date',
										})}`}
									</strong>
								</Typography>
							</Grid>
							<Grid item={true} xs={4}>
								<Typography variant='body2'>
									{
										<FormattedDate
											{...constants.dateFormatOptions}
											value={sharedService.getNewDateIfNotExist(user.joiningDate)}
										/>
									}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</>
		);
	};

	private addUserFormDialog = () => {
		return (
			<div>
				<Dialog
					open={!!this.state.isOpenAddUserDialog}
					onClose={() =>
						this.setState({
							isOpenAddUserDialog: false,
							addUserLoading: false,
						})
					}
					fullWidth={true}
				>
					<DialogTitle>{intlService.GetFormattedMessage('app.user.add-user-label')}</DialogTitle>
					<DialogContent>
						<Formik
							initialValues={this.initialAddUserFormValue}
							ref={this.formRef}
							onSubmit={async () => await this.addUser()}
							validationSchema={addUserSchema}
							enableReinitialize={true}
						>
							{(props: FormikProps<FormikValues>) => (
								<Form noValidate={true}>
									<Grid container={true} direction='column' spacing={2}>
										<Grid item={true} xs={12}>
											<Field
												name='firstName'
												label={intlService.GetFormattedMessage('app.signup.first-name-label')}
												component={TextField}
												type='text'
												fullWidth={true}
												variant='outlined'
												value={props.values.firstName}
												required={true}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													this.setState({
														firstName: e.target.value,
													});
													props.handleChange('firstName')(e);
												}}
												error={!!((props.values.firstName.length || props.submitCount) && !!props.errors.firstName)}
												helperText={props.values.firstName.length || props.submitCount ? props.errors.firstName : null}
											/>
										</Grid>
										{/* LastName */}

										<Grid item={true} xs={12}>
											<Field
												name='lastName'
												label={intlService.GetFormattedMessage('app.signup.last-name-label')}
												component={TextField}
												type='text'
												variant='outlined'
												fullWidth={true}
												value={props.values.lastName}
												required={true}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													this.setState({
														lastName: e.target.value,
													});
													props.handleChange('lastName')(e);
												}}
												error={!!((props.values.lastName.length || props.submitCount) && !!props.errors.lastName)}
												helperText={props.values.lastName.length || props.submitCount ? props.errors.lastName : null}
											/>
										</Grid>

										{/* Email */}

										<Grid item={true} xs={12}>
											<Field
												name='email'
												label={intlService.GetFormattedMessage('app.signup.email-label')}
												component={TextField}
												variant='outlined'
												type='text'
												fullWidth={true}
												value={props.values.email}
												required={true}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													this.setState({
														email: e.target.value,
													});
													props.handleChange('email')(e);
												}}
												error={!!((props.values.email.length || props.submitCount) && !!props.errors.email)}
												helperText={props.values.email.length || props.submitCount ? props.errors.email : null}
											/>
										</Grid>
									</Grid>
								</Form>
							)}
						</Formik>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								this.setState({
									isOpenAddUserDialog: false,
								});
							}}
							variant='outlined'
							color='primary'
						>
							{intlService.GetFormattedMessage('app.user.cancel-button')}
						</Button>
						<CustomButton
							onClick={() => {
								this.formRef.current.submitForm();
							}}
							buttonText={intlService.GetFormattedMessage('app.user.add-button')}
							type={ButtonTypeEnum.Button}
							loading={this.state.addUserLoading}
							disabled={this.state.addUserLoading}
						/>
					</DialogActions>
				</Dialog>
			</div>
		);
	};

	private openSnackbar = (message: string | string[], variantName: SnackBarVariants = SnackBarVariants.Success) => {
		this.setState({
			dataLoading: false,
		});

		this.props.openSnackBar(message, variantName);
	};

	private async addUser() {
		try {
			const user: models.IAddUser = {
				firstName: this.state.firstName,
				lastName: this.state.lastName,
				email: this.state.email,
				role: UserRole.User,
				accountId: this.props.accountId,
			};
			this.setState({
				addUserLoading: true,
			});
			const newUserResponse = await userService.addUser(user);
			if (!!newUserResponse) {
				const newUser: models.IAccountUserResponseVM = {
					...newUserResponse,
					userRole: newUserResponse.role,
					name: newUserResponse.firstName + ' ' + newUserResponse.lastName,
				};
				this.props.users.push(newUser);
				this.openSnackbar(
					this.props.intl.formatMessage({
						id: 'app.user.add-user-successfully',
					}),
					SnackBarVariants.Success
				);
			}
		} catch (err) {
			if (!Axios.isCancel(err)) {
				this.props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
			}
		} finally {
			this.setState({
				addUserLoading: false,
				isOpenAddUserDialog: false,
			});
		}
	}

	/**
	 * Delete User
	 */
	private async deleteUser(userId: string) {
		try {
			this.setState({
				deleteUserLoading: true,
			});
			await userService.deleteUser(userId);
			const index = this.props.users.findIndex((x) => x.id === userId);
			this.props.users.splice(index, 1);
			this.setState({
				deleteUserLoading: false,
			});
			this.openSnackbar(
				this.props.intl.formatMessage({
					id: 'app.user.delete-successfully',
				}),
				SnackBarVariants.Success
			);
		} catch (e) {
			this.setState({
				deleteUserLoading: false,
			});
			if (!Axios.isCancel(e)) {
				this.openSnackbar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	}

	/**
	 * Update User Email
	 */
	private async UpdateUserEmailByAdmin(userId: string, email: string) {
		try {
			this.setState({
				editUserLoading: true,
			});
			const userRequest: models.IUpdateUserEmail = {
				email,
			};
			const updatedUserEmailResponse = await adminUserService.updateUserEmailByAdmin(userId, userRequest);
			if (!!updatedUserEmailResponse) {
				const item: models.IAccountUserResponseVM = {
					...updatedUserEmailResponse,
				};
				const updatedUserIndex = this.props.users.findIndex((x) => x.id === userId);
				this.props.users[updatedUserIndex] = item;
				this.setState({
					isOpenEditDialog: false,
					editUserLoading: false,
				});
			}
			this.openSnackbar(
				this.props.intl.formatMessage({
					id: 'admin-account-user-list.update-email-successfully',
				}),
				SnackBarVariants.Success
			);
		} catch (e) {
			this.setState({
				editUserLoading: false,
			});
			if (!Axios.isCancel(e)) {
				this.openSnackbar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	}

	private async onResetPassword(email: string) {
		try {
			this.setState({ resetUserLoading: true });
			await accountService.userForgotPassword({ email });
			this.setState({
				isOpenResetDialog: false,
				resetUserLoading: false,
			});
			this.openSnackbar(
				this.props.intl.formatMessage({
					id: 'app.snackbar.reset-email-link',
				}),
				SnackBarVariants.Success
			);
		} catch (error) {
			this.setState({
				resetUserLoading: false,
				isOpenResetDialog: false,
			});
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private copyToClipboard = async (textToCopy: string) => {
		await navigator.clipboard.writeText(textToCopy);

		this.openSnackbar(
			this.props.intl.formatMessage({
				id: 'app.api-key.copied-to-clipboard',
			}),
			SnackBarVariants.Success
		);
	};

	private getUserDetails = (id: string) => {
		this.setState((prevState) => ({
			collapse: {
				...prevState.collapse,
				[id]: !prevState.collapse[id],
			},
		}));
	};
}
export default withSnackBar(withRouter(withAxiosCancellation(injectIntl(AdminAccountUserListComponent))));



// WEBPACK FOOTER //
// ./src/components/admin/customer/admin-account-user-list.component.tsx